import type { ReactNode } from 'react';
import { CalendarBlankIcon, MapPinIcon, UsersIcon } from '@phosphor-icons/react';
import { formatDateRange, parseDateOnly } from '@voll-intelligence/dates';
import { PersonAvatar } from '../../shared/person/person_row.js';
import {
  SummaryPill,
  type SummaryPillSegment,
  type SummaryPillTarget,
} from '../../shared/summary_pill/summary_pill.js';

/**
 * A pill de resumo da busca de HOTEL, a MESMA na página do web-chat e no
 * takeover "Ver todos os hotéis" da conversa: ocupação (com o retrato do
 * primeiro hóspede), destino e datas; a lupa re-busca o MESMO pedido. Quem
 * monta só traduz o pedido dele em `HotelSummary` e diz o que o clique faz (a
 * página volta ao formulário; o takeover expande a faixa de busca no lugar).
 */

export type HotelSummary = {
  place: string;
  /** A cidade que a busca resolveu, quando o lugar NÃO é uma cidade
   *  ("AVENIDA PAULISTA · Curitiba, PR"): o homônimo de outra cidade não passa
   *  pela pill sem dizer onde fica. */
  locality?: string;
  /** `YYYY-MM-DD`. */
  checkin?: string;
  checkout?: string;
  /** As contagens são do host: cada superfície conta do jeito do pedido dela. */
  guests: number;
  rooms: number;
  /** O primeiro hóspede; sem ele, o glifo de grupo. */
  lead?: { name: string; photoUrl?: string | null };
};

export type HotelSummaryPillCopy = {
  aria: string;
  /** A primeira linha do celular quando ainda não há destino. */
  title: string;
  /** "2 hóspedes · 1 quarto" — hóspedes primeiro, a ordem de todas as
   *  superfícies do hotel. */
  occupancy: (guests: number, rooms: number) => string;
};

export type HotelSummaryPillProps = {
  summary: HotelSummary;
  copy: HotelSummaryPillCopy;
  locale: string;
  readOnly?: boolean;
  onExpand?: (target: SummaryPillTarget) => void;
  /** A lupa e o nome dela, como na `SummaryPill`. Ausente ⇒ sem lupa. */
  search?: { onSearch: () => void; label: string };
};

/** "21–24 nov"; só a entrada ⇒ o dia dela. Vazio sem entrada válida. */
function stayLabel(summary: HotelSummary, locale: string): string {
  const start = parseDateOnly(summary.checkin);
  if (!start) return '';
  return formatDateRange(start, parseDateOnly(summary.checkout), locale, new Date());
}

export function HotelSummaryPill({
  summary,
  copy,
  locale,
  readOnly,
  onExpand,
  search,
}: Readonly<HotelSummaryPillProps>): ReactNode {
  const { lead } = summary;
  const occupancy = copy.occupancy(summary.guests, summary.rooms);
  const destination =
    summary.place && summary.locality ? `${summary.place} · ${summary.locality}` : summary.place;
  const dates = stayLabel(summary, locale);
  const segments: SummaryPillSegment[] = [
    {
      key: 'traveler',
      icon: lead ? (
        <PersonAvatar name={lead.name} photoUrl={lead.photoUrl ?? null} size="xs" locale={locale} />
      ) : (
        <UsersIcon size={14} aria-hidden />
      ),
      label: occupancy,
    },
    ...(destination
      ? [
          {
            key: 'destination' as const,
            icon: <MapPinIcon size={14} aria-hidden />,
            label: destination,
          },
        ]
      : []),
    ...(dates
      ? [{ key: 'dates' as const, icon: <CalendarBlankIcon size={14} aria-hidden />, label: dates }]
      : []),
  ];
  return (
    <SummaryPill
      segments={segments}
      // No celular, destino · datas em cima e a ocupação embaixo (#2660).
      compact={{
        primary: destination || copy.title,
        when: dates,
        secondary: occupancy,
      }}
      ariaLabel={copy.aria}
      {...(readOnly === undefined ? {} : { readOnly })}
      {...(onExpand ? { onExpand } : {})}
      {...(search ? { search } : {})}
    />
  );
}
// probe
